<template>
    <v-row justify="center" align="center">
        <v-col cols="12" sm="12" md="12">
            <import-page-banner
                title="Checkpoint"
                sample-csv-path="/addDirectOrderCheckpointByImport.csv"
            />

            <v-card>
                <v-card-title>
                    <v-row>
                        <v-col cols="12" sm="12" md="6"> Add Checkpoint by Import </v-col>
                    </v-row>
                </v-card-title>
                <v-card-text>
                    <p><b>Only accept csv file.</b></p>
                    <p>1. Choose your file</p>
                    <p>2. SELECT THE CORRECT MAPPING OF Field and Column</p>
                    <p>3. IMPORT</p>
                    <br />
                    <p>
                        <b
                            >IMPORTANT NOTES!! Checkpoint time is saved as UTC ISO 8601 format (e.g.
                            2199-12-31T23:59:59.999Z) in system. When you add checkpoint, you must use UTC ISO 8601
                            format. Date Time format displayed will be converted automatically to default local
                            (timezone) format by web browser.</b
                        >
                    </p>
                    <br />
                    <!-- New CSV Importer Component -->
                    <csv-importer
                        :field-definitions="data.fields"
                        :preview-headers="data.previewHeaders"
                        @data-loaded="onCsvDataLoaded"
                        @data-cleared="onCsvDataCleared"
                    />
                </v-card-text>
            </v-card>
            <br />
            <!-- Step 2a: Validation Preview -->
            <v-card v-if="data.mappedCsv && data.mappedCsv.length > 0 && !showGroupedData">
                <v-card-title>Data Validation & Preview</v-card-title>
                <v-card-text>
                    <v-table>
                        <thead>
                            <tr>
                                <th class="text-left" v-for="header in data.headers" :key="header.value">
                                    {{ header.text }}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                v-for="(item, idx) in transformMappedCsv(data.mappedCsv)"
                                :key="`rowcsv${idx}`"
                                :class="invalidRowClass(item)"
                            >
                                <td>{{ item.directOrderNumber }}</td>
                                <td>{{ item.checkpointTimeUtc }}</td>
                                <td>{{ item.tag }}</td>
                                <td>{{ item.subTag }}</td>
                                <td>{{ item.message }}</td>
                            </tr>
                        </tbody>
                    </v-table>
                </v-card-text>
                <v-card-actions>
                    <v-btn
                        class="mr-2"
                        color="success"
                        :loading="loading"
                        :disabled="loading || invalidFile"
                        @click="onNextClick"
                    >
                        Next
                    </v-btn>
                    <v-btn class="mr-2" :to="pathListDirectOrder"> Back to list </v-btn>
                </v-card-actions>
            </v-card>

            <!-- Step 2b: Grouped Preview & Import -->
            <v-card v-if="showGroupedData && data.groupedList && data.groupedList.length > 0">
                <v-card-title>Import Checkpoint</v-card-title>
                <v-card-text>
                    <!-- Grouped direct order cards -->
                    <v-card
                        v-for="item in data.groupedList"
                        :key="item.directOrderNumber"
                        class="mb-4"
                        :class="errorRowClass(item)"
                    >
                        <v-card-title class="d-flex justify-space-between align-center">
                            <div>
                                <v-icon>mdi-package-variant</v-icon>
                                <span class="ml-2">{{ item.directOrderNumber }}</span>
                            </div>
                            <v-chip
                                v-if="item.status"
                                :color="item.status === 'success' ? 'success' : 'error'"
                                size="small"
                            >
                                {{ item.status }}
                            </v-chip>
                        </v-card-title>

                        <v-card-text>
                            <!-- Error message if failed -->
                            <v-alert
                                v-if="item.error"
                                type="error"
                                density="compact"
                                class="mb-3"
                            >
                                {{ item.error }}
                            </v-alert>

                            <!-- Checkpoint list -->
                            <div class="checkpoint-list">
                                <v-card
                                    v-for="(checkpoint, idx) in item.directOrderCheckpointList"
                                    :key="`cp-${idx}`"
                                    outlined
                                    class="mb-2"
                                >
                                    <v-card-text>
                                        <!-- Timestamp -->
                                        <div class="d-flex align-center mb-2">
                                            <v-icon size="small" class="mr-2">mdi-clock-outline</v-icon>
                                            <strong>{{ checkpoint.checkpointTime }}</strong>
                                        </div>
                                        <div class="text-caption text-grey mb-3">
                                            {{ formLabelText.checkpointTimeLocal }}:
                                            {{ formatTime(checkpoint.checkpointTime) }}
                                        </div>

                                        <!-- Tags -->
                                        <div class="mb-2">
                                            <v-chip size="small" color="primary" class="mr-2">
                                                {{ checkpoint.tag }}
                                            </v-chip>
                                            <v-chip size="small" color="secondary">
                                                {{ checkpoint.subTag }}
                                            </v-chip>
                                        </div>

                                        <!-- Message -->
                                        <div class="text-body-2 text-grey-darken-2">
                                            <v-icon size="small" class="mr-1">mdi-message-text</v-icon>
                                            {{ checkpoint.message }}
                                        </div>
                                    </v-card-text>
                                </v-card>
                            </div>
                        </v-card-text>
                    </v-card>
                </v-card-text>
                <v-card-actions>
                    <v-btn
                        class="mr-2"
                        color="primary"
                        :loading="loading"
                        :disabled="loading || invalidFile"
                        @click="onImportClick"
                    >
                        <v-icon>mdi-cloud-upload</v-icon>
                        Import
                    </v-btn>
                    <v-btn class="mr-2" :to="pathListDirectOrder"> Back to list </v-btn>
                    <span v-if="importDone">{{ importDoneMsg }} </span>
                </v-card-actions>
            </v-card>
        </v-col>
    </v-row>
</template>

<script lang="ts">
import { defineComponent, reactive } from 'vue';
import { fetchWrapper } from '../../utils/FetchWrapper';
import { DirectOrderCheckpointLabelTextHelper } from '../../utils/labelTextHelper/DirectOrderCheckpointLabelTextHelper';
import { DirectOrderCheckpointHeaderHelper } from '../../utils/DirectOrderCheckpointHeaderHelper';
import * as _ from 'lodash';
import { PathDefinition } from '../../utils/PathDefinition';
import { ImportTableLabelTextHelper } from '../../utils/labelTextHelper/ImportTableLabelTextHelper';
import { SharedMsgHelper } from '../../utils/SharedMsgHelper';
import { Formatter } from '../../utils/Formatter';
import CsvImporter from '../../components/CsvImporter.vue';
import ImportPageBanner from '../../components/ImportPageBanner.vue';
import { ImportValidationHelper } from '../../utils/ImportValidationHelper';
import { DirectOrderCheckpointImportValidation } from '../../config/importValidationConfigs';

export default defineComponent({
    name: 'ImportDirectOrderCheckpoint',

    components: {
        CsvImporter,
        ImportPageBanner
    },

    setup() {
        const data = reactive({
            mappedCsv: null,
            groupedList: null,
            fields: DirectOrderCheckpointHeaderHelper.getCheckpointFields(),
            previewHeaders: DirectOrderCheckpointHeaderHelper.getPreviewHeaders(),
            headers: DirectOrderCheckpointHeaderHelper.getPreviewHeaders(),
            nextHeaders: DirectOrderCheckpointHeaderHelper.getImportSubmitHeaders()
        });
        return {
            data
        };
    },
    data() {
        return {
            loading: false,
            rowErrorClass: 'row-validation-error',
            showGroupedData: false,
            invalidFile: false,
            importDone: false,
            importDoneMsg: SharedMsgHelper.getImportDoneMsg,
            pathListDirectOrder: PathDefinition.listDirectOrder,
            formLabelText: DirectOrderCheckpointLabelTextHelper.getLabelText
        };
    },
    methods: {
        onCsvDataLoaded(mappedData) {
            // Receive data from CSV importer component
            this.data.mappedCsv = mappedData;
        },
        onCsvDataCleared() {
            // Clear data when file is removed
            this.data.mappedCsv = null;
            this.data.groupedList = null;
            this.showGroupedData = false;
            this.importDone = false;
            this.invalidFile = false;
        },
        transformMappedCsv(mappedCsv) {
            return ImportValidationHelper.validateRows(mappedCsv, DirectOrderCheckpointImportValidation);
        },
        groupByDirectOrderNumber(mappedCsv) {
            return _.chain(mappedCsv)
                .groupBy('directOrderNumber')
                .map((value, key) => {
                    return {
                        directOrderNumber: key,
                        directOrderCheckpointList: _.map(value, (item) => {
                            return {
                                checkpointTime: item.checkpointTimeUtc,
                                tag: item.tag,
                                subTag: item.subTag,
                                message: item.message
                            };
                        })
                    };
                })
                .value();
        },
        formatTime: function (iso8601Time: string) {
            return Formatter.formatTimeToDefault(iso8601Time);
        },
        async onNextClick() {
            this.data.mappedCsv = this.transformMappedCsv(this.data.mappedCsv);
            const isValid = this.validation(this.data.mappedCsv);
            if (isValid) {
                this.data.groupedList = this.groupByDirectOrderNumber(this.data.mappedCsv);
                this.showGroupedData = true;
            } else {
                // Validation failed - errors are already highlighted in the table
                this.showGroupedData = false;
            }
        },
        validation(list) {
            return _.every(list, (v) => {
                return !this.isInvalidRow(v);
            });
        },
        invalidRowClass(item) {
            if (this.isInvalidRow(item)) {
                return this.rowErrorClass;
            }
            return '';
        },
        isInvalidRow(item) {
            return this.isInvalidRowContent(item);
        },
        isInvalidRowContent(item) {
            return ImportValidationHelper.hasValidationErrors(item, DirectOrderCheckpointImportValidation);
        },
        errorRowClass(item) {
            if (item.status === ImportTableLabelTextHelper.getImportResultValue.error) {
                return this.rowErrorClass;
            }
            return '';
        },
        async onImportClick() {
            if (this.data.groupedList && this.data.groupedList.length > 0) {
                await this.importSubmit();
            }
        },
        async importSubmit() {
            this.loading = true;
            for (const item in this.data.groupedList) {
                await this.add(item);
            }
            this.importDone = true;
            // prevent import again
            this.invalidFile = true;
            this.loading = false;
        },
        async add(idx) {
            const csvData = this.data.groupedList[idx];
            const response = await this.addDirectOrderCheckpointList(csvData);
            this.processResponse(response, idx, csvData);
        },
        async addDirectOrderCheckpointList(csvData) {
            const request = _.pick(csvData, ['directOrderNumber', 'directOrderCheckpointList']);
            const requestUrl = `/api/operation/directOrder/addDirectOrderCheckpoint`;
            return await fetchWrapper.post(requestUrl, request);
        },
        processResponse(response, idx, csvData) {
            if (response.success) {
                csvData.status = ImportTableLabelTextHelper.getImportResultValue.success;
            } else {
                csvData.status = ImportTableLabelTextHelper.getImportResultValue.error;
                csvData.error = response.errors;
            }
            this.data.groupedList.splice(idx, 1, csvData);
        }
    }
});
</script>

<style scoped>
select {
    border: 1px solid !important;
}

.row-validation-error {
    background-color: #ffebee;
}

.checkpoint-list {
    margin-top: 8px;
}
</style>
